/**
 * The Skills tab, laid out like Cursor's Automations page: pill tabs for the
 * firm's skills, mine, and Explore. Firm opens with the built-in starters as
 * cards above one outlined table (Skill, Visas, Status, Automations and a
 * menu with Run and Enable); Mine is the same table cut to what I wrote;
 * Explore holds every starter, grouped by practice area. A starter the firm
 * already set up (`starter_id`) shows as in use and opens that skill. The
 * list never creates: a starter, Duplicate or New Automation opens the skill page
 * (`?new=1`), and `?skill=` or `?from_thread=` swap the tab for that page and
 * its builder.
 */
import type { ColumnDef } from "@tanstack/react-table";
import {
  Archive,
  ArrowRight,
  Copy,
  FlaskConical,
  Link2,
  MoreHorizontal,
  Play,
  RotateCw,
  Sparkles,
  Trash2
} from "lucide-react";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { IconTile } from "@/components/ui/icon-tile";
import { ListSearch } from "@/components/ui/list-toolbar";
import { SectionHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag } from "@/components/ui/tag";
import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import {
  RUN_PARAM,
  THREAD_PARAM
} from "@/features/caseIde/chat/useThreadDeepLink";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import { cn } from "@/lib/utils";

import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmKnowledgeScope } from "@/features/firmKnowledge/hooks/useFirmScope";
import type { FirmLibraryState } from "@/features/firmKnowledge/hooks/useFirmLibrary";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import { useSkillOptions } from "@/features/firmKnowledge/hooks/useSkillOptions";
import {
  automationsPath,
  skillScopeOf,
  type SkillScope
} from "@/features/automations/routes";
import type { FirmSkill } from "@/features/firmKnowledge/types";
import { skillVisas } from "./caseSuggestions";
import { DeleteSkillDialog } from "./DeleteSkillDialog";
import { RunSkillDialog, type RunSkillRequest } from "./RunSkillDialog";
import { SkillBuilderView } from "./SkillBuilderView";
import {
  eventLabelOf,
  FEATURED_STARTERS,
  filterSkills,
  fullDate,
  lastRunLabel,
  lastUseLabel,
  shortDate,
  SKILL_STATUS_LABEL,
  STARTER_CATEGORIES,
  STARTER_SKILLS,
  triggerChip,
  type StarterSkill
} from "./skillModel";

/**
 * Cursor's "From Cursor" card: a round icon, one action, a two-line blurb.
 * Once the firm has set the starter up, the action opens that skill instead.
 */
const BuiltInCard: FC<{
  starter: StarterSkill;
  adopted: FirmSkill | null;
  onSetUp: (starterId: string) => void;
  onOpen: (skillId: string) => void;
}> = ({ starter, adopted, onSetUp, onOpen }) => (
  <div
    className="flex flex-col gap-3 rounded-xl bg-elevated p-4 ring-1 ring-line-2"
    data-testid={`skill-builtin-${starter.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <IconTile icon={starter.icon} size="lg" className="rounded-full" />
      <span className="flex items-center gap-2">
        {adopted && adopted.status !== "active" ? (
          <span
            className="text-sm text-fg-3"
            data-testid={`skill-builtin-off-${starter.id}`}
          >
            Off
          </span>
        ) : null}
        <Button
          size="xs"
          variant="pill"
          onClick={() => (adopted ? onOpen(adopted.id) : onSetUp(starter.id))}
          data-testid={`skill-builtin-setup-${starter.id}`}
        >
          {adopted ? "Manage" : "Set Up"}
        </Button>
      </span>
    </div>
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-base text-fg">{starter.title}</span>
      <span className="line-clamp-2 text-sm text-fg-3">{starter.blurb}</span>
    </div>
  </div>
);

/**
 * Cursor's Explore template: the whole card opens a new skill prefilled from
 * it, or the firm's skill when the starter is already in use.
 */
const StarterCard: FC<{
  starter: StarterSkill;
  adopted: FirmSkill | null;
  onPick: (starterId: string) => void;
  onOpen: (skillId: string) => void;
}> = ({ starter, adopted, onPick, onOpen }) => {
  const Icon = starter.icon;
  return (
    <button
      type="button"
      onClick={() => (adopted ? onOpen(adopted.id) : onPick(starter.id))}
      aria-label={
        adopted ? `Open ${starter.title}` : `Start With ${starter.title}`
      }
      className="group relative flex flex-col gap-3 rounded-xl bg-elevated p-4 text-left ring-1 ring-line-2 transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40"
      data-testid={`skill-starter-${starter.id}`}
    >
      {adopted ? (
        <Tag
          className="absolute top-3 right-3"
          data-testid={`skill-starter-in-use-${starter.id}`}
        >
          In Use
        </Tag>
      ) : (
        <Tag
          aria-hidden
          className="absolute top-3 right-3 opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          Use
        </Tag>
      )}
      <Icon className="size-4 text-accent-seed" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-base text-fg">{starter.title}</span>
        <span className="line-clamp-2 text-sm text-fg-3">{starter.blurb}</span>
      </span>
      <span className="mt-auto flex min-w-0 items-center gap-1.5 text-sm text-fg-3">
        <span className="truncate">{starter.when}</span>
        <ArrowRight className="size-3 shrink-0 text-fg-4" />
        <span className="truncate">{starter.makes}</span>
      </span>
    </button>
  );
};

const SkillStatus: FC<{ status: FirmSkill["status"] }> = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5",
      status === "active" ? "text-fg" : "text-fg-2"
    )}
    data-testid="skill-status"
    data-status={status}
  >
    <span
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        status === "active" ? "bg-success" : "bg-fg-4"
      )}
      aria-hidden
    />
    {SKILL_STATUS_LABEL[status]}
  </span>
);

type Props = {
  skills: FirmSkillsState;
  library: FirmLibraryState;
  firm: FirmKnowledgeScope;
  /** The Library / Skills segment, first in the tab row. */
  toolbarLeading?: ReactNode;
};

export const SkillsTab: FC<Props> = ({
  skills,
  library,
  firm,
  toolbarLeading
}) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { practiceScope, loading: specsLoading } = useAgentSpecs();
  const { members, me } = useTeamMembers();
  const options = useSkillOptions(firm.scope);
  const building =
    params.has("skill") ||
    params.has("from_thread") ||
    params.get("new") === "1";
  const requested = skillScopeOf(`?${params.toString()}`);
  const ready = firm.permissionsReady;
  const scope: SkillScope =
    requested === "explore" && ready && !firm.canEdit ? "firm" : requested;
  const [running, setRunning] = useState<{
    skill: FirmSkill;
    test: boolean;
  } | null>(null);
  const [deleting, setDeleting] = useState<FirmSkill | null>(null);
  const [query, setQuery] = useState("");

  const adoptedBy = (starterId: string): FirmSkill | null =>
    skills.skills.find((s) => s.starter_id === starterId) ?? null;
  const openSkill = (skillId: string) => navigate(automationsPath({ skillId }));

  const inScope = useMemo(
    () =>
      scope === "mine"
        ? skills.skills.filter((s) => s.created_by === me.uid)
        : skills.skills,
    [skills.skills, scope, me.uid]
  );
  const shown = useMemo(
    () => filterSkills(inScope, { query, status: "", sort: "updated" }),
    [inScope, query]
  );

  const switchScope = (next: SkillScope) => {
    const search = new URLSearchParams(params);
    if (next === "firm") search.delete("scope");
    else search.set("scope", next);
    navigate({ search: search.toString() });
  };

  const startFrom = (starterId: string) =>
    navigate(automationsPath({ newSkill: true, starter: starterId }));

  const ownerName = (uid: string | null) =>
    uid === me.uid
      ? me.name || "You"
      : members.find((m) => m.uid === uid)?.label ?? null;
  const eventLabel = (action: string) => eventLabelOf(options, action);

  const copyLink = async (skill: FirmSkill) => {
    const url = `${window.location.origin}${automationsPath({
      skillId: skill.id
    })}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const toggle = async (skill: FirmSkill, enabled: boolean) => {
    try {
      await skills.setStatus(skill.id, enabled ? "active" : "archived");
      invalidateActiveFirmSkills();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not update the automation."
      );
    }
  };

  const runOn = async (
    skill: FirmSkill,
    { ref, inputs, mode, test }: RunSkillRequest
  ) => {
    if (!ref) return;
    try {
      const started = await firmKnowledgeApi.startSkillRun(
        skill.id,
        {
          individual_id: ref.individualId,
          inputs,
          mode,
          ...(test ? { test: true } : {})
        },
        firm.scope
      );
      setRunning(null);
      const search = new URLSearchParams({
        [THREAD_PARAM]: started.thread_id,
        [RUN_PARAM]: started.run.id
      });
      navigate(`${caseWorkspacePath(ref)}?${search.toString()}`, {
        state: { skillRunSeed: started.seed_message }
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start the run.");
    }
  };

  const columns: ColumnDef<FirmSkill, unknown>[] = [
    {
      id: "skill",
      header: "Automation",
      meta: { className: "w-full max-w-0" },
      cell: ({ row }) => {
        const skill = row.original;
        const owner = ownerName(skill.created_by);
        const when = shortDate(skill.updated_at ?? skill.created_at);
        return (
          <span className="flex min-w-0 flex-col">
            <Link
              to={automationsPath({ skillId: skill.id })}
              onClick={(e) => e.stopPropagation()}
              className="truncate rounded-xs text-fg outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid="skill-row-link"
            >
              {skill.title || skill.slug}
            </Link>
            <span className="truncate text-sm text-fg-3">
              {owner ? `By ${owner}` : null}
              {owner && when ? " " : null}
              {when ? <span className="text-fg-4">{when}</span> : null}
            </span>
          </span>
        );
      }
    },
    {
      id: "visas",
      header: "Visas",
      meta: { className: "w-px whitespace-nowrap" },
      cell: ({ row }) => {
        const visas = skillVisas(row.original);
        return visas.length ? (
          <span className="block max-w-40 truncate text-fg-2">
            {visas.join(", ")}
          </span>
        ) : (
          <span className="text-fg-4">Any Visa</span>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      meta: { className: "w-px whitespace-nowrap" },
      cell: ({ row }) => <SkillStatus status={row.original.status} />
    },
    {
      id: "automations",
      header: "Automations",
      meta: { className: "w-px whitespace-nowrap" },
      cell: ({ row }) => {
        const on = row.original.triggers.filter((t) => t.enabled);
        if (!on.length) return <span className="text-fg-4">By Hand</span>;
        const first = triggerChip(on[0], eventLabel);
        return (
          <span className="flex max-w-56 items-center gap-1">
            <Tag className="min-w-0" title={first}>
              {first}
            </Tag>
            {on.length > 1 ? <Tag>+{on.length - 1}</Tag> : null}
          </span>
        );
      }
    },
    {
      id: "last-run",
      header: "Last Used",
      meta: { className: "w-px whitespace-nowrap" },
      cell: ({ row }) => {
        const skill = row.original;
        const label = lastUseLabel(skill);
        return label ? (
          <span
            className="text-fg-2"
            title={fullDate(
              lastRunLabel(skill.last_run) === label
                ? skill.last_run?.started_at
                : skill.last_used_at
            )}
            data-testid="skill-last-run"
          >
            {label}
          </span>
        ) : (
          <span className="text-fg-4" data-testid="skill-last-run">
            Never
          </span>
        );
      }
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { className: "w-px whitespace-nowrap" },
      cell: ({ row }) => {
        const skill = row.original;
        const active = skill.status === "active";
        return (
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`More for ${skill.title || skill.slug}`}
                  data-testid={`skill-menu-${skill.id}`}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuItem
                  onSelect={() => openSkill(skill.id)}
                  data-testid={`skill-open-${skill.id}`}
                >
                  <ArrowRight /> Open
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!active}
                  onSelect={() => setRunning({ skill, test: false })}
                  data-testid={`skill-run-${skill.id}`}
                >
                  <Play />
                  <span className="flex flex-col">
                    Run on a Case
                    {!active ? (
                      <span className="text-sm text-fg-3">
                        Enable it to run
                      </span>
                    ) : null}
                  </span>
                </DropdownMenuItem>
                {firm.canEdit ? (
                  <>
                    <DropdownMenuItem
                      disabled={skill.status === "archived"}
                      onSelect={() => setRunning({ skill, test: true })}
                      data-testid={`skill-test-${skill.id}`}
                    >
                      <FlaskConical /> Test
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        navigate(
                          automationsPath({
                            newSkill: true,
                            duplicate: skill.id
                          })
                        )
                      }
                      data-testid={`skill-duplicate-${skill.id}`}
                    >
                      <Copy /> Duplicate
                    </DropdownMenuItem>
                  </>
                ) : null}
                <DropdownMenuItem
                  onSelect={() => void copyLink(skill)}
                  data-testid={`skill-copy-link-${skill.id}`}
                >
                  <Link2 /> Copy Link
                </DropdownMenuItem>
                {firm.canEdit ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => void toggle(skill, !active)}
                      data-testid={`skill-enable-${skill.id}`}
                    >
                      {active ? <Archive /> : <Sparkles />}
                      {active ? "Archive" : "Enable"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(skill)}
                      data-testid={`skill-delete-${skill.id}`}
                    >
                      <Trash2 /> Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      }
    }
  ];

  if (building) {
    if (!practiceScope) {
      return (
        <div className="flex h-full items-center justify-center text-base text-fg-3">
          {specsLoading ? (
            <span className="inline-flex items-center gap-2">
              <Spinner className="size-3.5" /> Loading the builder…
            </span>
          ) : (
            "The automation builder needs a practice to work in."
          )}
        </div>
      );
    }
    return (
      <SkillBuilderView
        practiceScope={practiceScope}
        skills={skills}
        firm={firm}
        library={library.active}
      />
    );
  }

  const total = inScope.length;
  const isLoading = skills.loading && skills.skills.length === 0;
  const searching = Boolean(query.trim());

  let table: ReactNode;
  if (skills.error) {
    table = (
      <EmptyState
        icon={RotateCw}
        title="Couldn’t Load Your Automations"
        description={skills.error}
        action={
          <Button
            size="sm"
            onClick={() => void skills.refresh()}
            disabled={skills.loading}
          >
            {skills.loading ? <Spinner className="size-3.5" /> : <RotateCw />}
            Try Again
          </Button>
        }
        className="py-16"
        role="alert"
        data-testid="skills-error"
      />
    );
  } else if (!isLoading && !ready && total === 0) {
    table = <Skeleton className="h-40 rounded-xl" />;
  } else if (!isLoading && total === 0) {
    table = (
      <EmptyState
        icon={Sparkles}
        title={
          scope === "mine"
            ? "You Haven’t Written an Automation"
            : "No Automations Yet"
        }
        description={
          firm.canEdit
            ? "Set up a built-in automation above, start from one in Explore, or save one from a case conversation (right-click its tab in the case chat)."
            : "Your firm hasn't given your role permission to write automations. Ask an owner or admin."
        }
        action={
          firm.canEdit ? (
            <Button size="sm" onClick={() => switchScope("explore")}>
              Explore Starters
            </Button>
          ) : undefined
        }
        className="py-16"
        data-testid="skills-empty"
      />
    );
  } else if (!isLoading && shown.length === 0) {
    table = (
      <EmptyState
        icon={Sparkles}
        title="No Automations Match"
        description="Try a different search."
        action={
          <Button size="sm" onClick={() => setQuery("")}>
            Clear Search
          </Button>
        }
        className="py-16"
        data-testid="skills-no-match"
      />
    );
  } else {
    table = (
      <DataTable
        variant="card"
        data={shown}
        columns={columns}
        rowHeight="double"
        getRowId={(s) => s.id}
        getRowTestId={() => "skill-row"}
        onRowClick={(s) => navigate(automationsPath({ skillId: s.id }))}
        isLoading={isLoading}
        stickyHeader={false}
      />
    );
  }

  const featured = STARTER_SKILLS.filter((s) =>
    FEATURED_STARTERS.includes(s.id)
  );

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="skills-tab">
      <div
        className="flex w-full flex-none items-center gap-3 px-6 pb-4"
        data-testid="skills-scopes"
      >
        {toolbarLeading}
        <span className="h-4 w-px bg-line-2" aria-hidden />
        {ready ? (
          <Tabs
            value={scope}
            onValueChange={(v) => switchScope(v as SkillScope)}
          >
            <TabsList variant="pill">
              <TabsTrigger value="firm" data-testid="skills-scope-firm">
                Firm
              </TabsTrigger>
              <TabsTrigger value="mine" data-testid="skills-scope-mine">
                Mine
              </TabsTrigger>
              {firm.canEdit ? (
                <TabsTrigger value="explore" data-testid="skills-scope-explore">
                  Explore
                </TabsTrigger>
              ) : null}
            </TabsList>
          </Tabs>
        ) : (
          <Skeleton
            className="h-7 w-48 rounded-full"
            data-testid="skills-scopes-loading"
          />
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto px-6 pb-8">
        {scope === "explore" && !ready ? (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : scope === "explore" ? (
          <>
            {STARTER_CATEGORIES.map((category) => {
              const starters = STARTER_SKILLS.filter(
                (s) => s.category === category
              );
              if (!starters.length) return null;
              return (
                <section
                  key={category}
                  className="flex flex-col gap-3"
                  data-testid="skill-starter-group"
                >
                  <SectionHeader title={category} />
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {starters.map((starter) => (
                      <StarterCard
                        key={starter.id}
                        starter={starter}
                        adopted={adoptedBy(starter.id)}
                        onPick={startFrom}
                        onOpen={openSkill}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        ) : (
          <>
            {scope === "firm" && !ready ? (
              <div
                className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="skill-builtins-loading"
              >
                {featured.map((s) => (
                  <Skeleton key={s.id} className="h-28 rounded-xl" />
                ))}
              </div>
            ) : null}
            {scope === "firm" && ready && firm.canEdit ? (
              <section
                className="flex flex-col gap-3"
                data-testid="skill-builtins"
              >
                <SectionHeader
                  title={
                    <span className="font-normal text-fg-3">
                      Built In From CaseBlink
                    </span>
                  }
                  actions={
                    <Button
                      variant="link"
                      size="xs"
                      className="text-fg-3"
                      onClick={() => switchScope("explore")}
                    >
                      See All in Explore
                    </Button>
                  }
                />
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {featured.map((starter) => (
                    <BuiltInCard
                      key={starter.id}
                      starter={starter}
                      adopted={adoptedBy(starter.id)}
                      onSetUp={startFrom}
                      onOpen={openSkill}
                    />
                  ))}
                </div>
              </section>
            ) : null}
            <section className="flex flex-col gap-2">
              <div
                className="flex items-center gap-3"
                data-testid="skills-toolbar"
              >
                <span className="flex-1 text-base text-fg-3 tabular-nums">
                  {total > 0
                    ? searching
                      ? `${shown.length} of ${total}`
                      : `Showing ${total}`
                    : null}
                </span>
                {total > 0 ? (
                  <ListSearch
                    className="w-56 shrink"
                    placeholder="Search automations…"
                    aria-label="Search automations"
                    value={query}
                    onValueChange={setQuery}
                    data-testid="skills-search"
                  />
                ) : null}
              </div>
              {table}
            </section>
          </>
        )}
      </div>

      <RunSkillDialog
        open={running !== null}
        onOpenChange={(open) => {
          if (!open) setRunning(null);
        }}
        test={running?.test ?? false}
        skill={
          running
            ? {
                title: running.skill.title || running.skill.slug,
                inputs: running.skill.inputs,
                defaultMode: running.skill.authority.default_mode,
                visas: skillVisas(running.skill)
              }
            : null
        }
        onStart={(request) => {
          if (running) return runOn(running.skill, request);
          return undefined;
        }}
      />
      <DeleteSkillDialog
        skill={deleting}
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        skills={skills}
      />
    </div>
  );
};
